import type { SyntheticEvent } from 'react';
import React, { useEffect, useMemo } from 'react';
import { useFormikContext } from 'formik';
import debounce from 'lodash/debounce';
import { SchemaTypesUtil } from '@axiom/types';
import set from 'lodash/set';
import type { z } from 'zod';

import { FormSchemaUtil } from '../../../utils/form-schema-util';
import { DesignSysEnvUtil } from '../../../utils/design-sys-env-util';

import { invokeFormContext } from './useFormContext';
import type { FormProps, FormPropsChildren } from './form.types';

export const RawForm = <Schema extends z.ZodTypeAny>({
  cancelOnEscape = false,
  children,
  dirty,
  handleReset,
  handleSubmit,
  isSubmitting,
  isValid,
  name,
  onChange,
  schema,
  setTouched,
  setFieldTouched,
  setValues,
  submitCount,
  submitOnChange,
  values,
}: {
  cancelOnEscape?: FormProps<Schema>['cancelOnEscape'];
  children: FormProps<Schema>['children'];
  dirty: FormPropsChildren<z.infer<Schema>>['dirty'];
  handleReset: (e?: SyntheticEvent<unknown>) => void;
  handleSubmit: (e?: SyntheticEvent<unknown>) => void | Promise<void>;
  isSubmitting: boolean;
  isValid: FormPropsChildren<z.infer<Schema>>['isValid'];
  name: string;
  onChange?: FormProps<Schema>['onChange'];
  schema: FormProps<Schema>['schema'];
  setTouched: FormPropsChildren<z.infer<Schema>>['setTouched'];
  setFieldTouched: FormPropsChildren<z.infer<Schema>>['setFieldTouched'];
  setValues: FormPropsChildren<z.infer<Schema>>['setValues'];
  submitCount: number;
  submitOnChange?: boolean;
  values: FormPropsChildren<z.infer<Schema>>['values'];
}) => {
  const FormContext = invokeFormContext();
  const formik = useFormikContext();

  const formattedSchema = useMemo(
    () => SchemaTypesUtil.format(schema),
    [schema]
  );

  const debouncedSubmit = useMemo(
    () => debounce(async () => formik.submitForm(), 500),
    [formik.submitForm]
  );

  const debounceChange = useMemo(
    () =>
      debounce((propName: string, changedValue: unknown) => {
        if (onChange) {
          const newChangedValue = set({}, propName, changedValue);

          onChange(newChangedValue);
        }
      }, DesignSysEnvUtil.typingDebounce),
    [onChange, formik.values, values]
  );

  useEffect(() => {
    if (submitOnChange && formik.dirty) {
      void debouncedSubmit();
    }
  }, [formik.values]);

  const formStatus =
    submitCount > 0 && !isSubmitting ? 'form-submitted' : 'form-active';
  const formProviderValue = useMemo(
    () => ({
      getSchemaProperty: (propName: string) => {
        return FormSchemaUtil.findNode(formattedSchema, propName);
      },
      fireReset: handleReset,
      fireSubmit: handleSubmit,
      fireChange: debounceChange,
      setFieldTouched,
      setValues,
      values,
    }),
    [handleReset, handleSubmit, values]
  );

  return (
    <div data-test={name}>
      <FormContext.Provider value={formProviderValue}>
        <div
          data-test={formStatus}
          onKeyDown={event => {
            /*
              Nothing while a write is on its way: the fields are the caller's to put back, and
              the request cannot be taken back with them.
            */
            if (!cancelOnEscape || event.key !== 'Escape' || isSubmitting) {
              return;
            }

            // Handled here, so it is not also a page-level gesture on its way out.
            event.preventDefault();
            event.stopPropagation();

            handleReset();

            if (typeof cancelOnEscape === 'function') cancelOnEscape();
          }}
        >
          <form
            noValidate
            onSubmit={event => {
              void handleSubmit(event);
            }}
          >
            {children({
              dirty,
              fireReset: handleReset,
              fireSubmit: handleSubmit,
              fireChange: (field, changedValue) => {
                debounceChange(field, changedValue);
              },
              isValid,
              setTouched,
              setFieldTouched,
              setValues,
              submitCount,
              values,
            })}
          </form>
        </div>
      </FormContext.Provider>
    </div>
  );
};
